import { useState, useEffect, useCallback, FormEvent } from "react";
import { useAdminAuth } from "@/lib/adminAuth";

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function toInputDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toISOString().slice(0, 10);
}

interface Coupon {
  id: number;
  code: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  startDate: string;
  expirationDate: string | null;
  usageLimit: number | null;
  usageCount: number;
  createdAt: string;
}

interface CouponUsage {
  id: number;
  customerName: string;
  company: string | null;
  usedAt: string;
}

const EMPTY_FORM = {
  code: "",
  discountType: "percent" as "percent" | "fixed",
  discountValue: 10,
  startDate: new Date().toISOString().slice(0, 10),
  expirationDate: "",
  usageLimit: "",
};

function getCouponStatus(c: Coupon): { label: string; color: string } {
  const now = new Date();
  if (new Date(c.startDate) > now) return { label: "Scheduled", color: "text-yellow-300 bg-yellow-500/15 border-yellow-400/30" };
  if (c.expirationDate && new Date(c.expirationDate) < now) return { label: "Expired", color: "text-slate-400 bg-slate-500/15 border-slate-400/30" };
  if (c.usageLimit !== null && c.usageCount >= c.usageLimit) return { label: "Limit reached", color: "text-red-400 bg-red-500/15 border-red-400/30" };
  return { label: "Active", color: "text-cyan-300 bg-cyan-500/15 border-cyan-400/30" };
}

export default function CouponsTab() {
  const { adminFetch } = useAdminAuth();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [usageCoupon, setUsageCoupon] = useState<Coupon | null>(null);
  const [usages, setUsages] = useState<CouponUsage[]>([]);
  const [loadingUsages, setLoadingUsages] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("api/admin/coupons");
      if (!res.ok) throw new Error("Failed to load coupons");
      setCoupons(await res.json());
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (c: Coupon) => {
    setEditing(c);
    setForm({
      code: c.code,
      discountType: c.discountType,
      discountValue: c.discountValue,
      startDate: toInputDate(c.startDate),
      expirationDate: toInputDate(c.expirationDate),
      usageLimit: c.usageLimit != null ? String(c.usageLimit) : "",
    });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        code: form.code.toUpperCase().trim(),
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        startDate: new Date(form.startDate).toISOString(),
        expirationDate: form.expirationDate ? new Date(form.expirationDate).toISOString() : null,
        usageLimit: form.usageLimit !== "" ? Number(form.usageLimit) : null,
      };
      const res = editing
        ? await adminFetch(`api/admin/coupons/${editing.id}`, { method: "PUT", body: JSON.stringify(body) })
        : await adminFetch("api/admin/coupons", { method: "POST", body: JSON.stringify(body) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? "Save failed");
      }
      closeForm();
      await load();
    } catch (e) {
      alert(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await adminFetch(`api/admin/coupons/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      setDeleteId(null);
      await load();
    } catch (e) {
      alert(errMsg(e));
    }
  };

  const openUsages = async (c: Coupon) => {
    setUsageCoupon(c);
    setLoadingUsages(true);
    try {
      const res = await adminFetch(`api/admin/coupons/${c.id}/usages`);
      setUsages(res.ok ? await res.json() : []);
    } catch {
      setUsages([]);
    } finally {
      setLoadingUsages(false);
    }
  };

  const inputClass = "w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500";

  if (loading) return <div className="text-white/50 py-8 text-center">Loading coupons…</div>;
  if (error) return <div className="text-red-400 py-8 text-center">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Coupons ({coupons.length})</h2>
        <button
          onClick={openCreate}
          className="bg-purple-600 hover:bg-purple-500 text-white text-sm px-4 py-2 rounded-lg transition"
        >
          + New Coupon
        </button>
      </div>

      {coupons.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-12 text-center">
          <div className="text-white/40 text-sm mb-1">No coupons yet</div>
          <div className="text-white/30 text-xs">Create your first discount coupon above.</div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/50 text-left">
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Discount</th>
                <th className="px-4 py-3 font-medium">Start</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium">Limit / Used</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => {
                const status = getCouponStatus(c);
                return (
                  <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openUsages(c)}
                        className="font-mono font-bold text-cyan-300 hover:text-cyan-200 underline underline-offset-2 transition text-sm"
                      >
                        {c.code}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-white">
                      {c.discountType === "percent"
                        ? `${c.discountValue}%`
                        : `$${c.discountValue.toLocaleString()}`}
                    </td>
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">{formatDate(c.startDate)}</td>
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">
                      {c.expirationDate ? formatDate(c.expirationDate) : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      {c.usageLimit != null ? `${c.usageCount} / ${c.usageLimit}` : `${c.usageCount} / ∞`}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block border rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEdit(c)}
                          className="text-purple-400 hover:text-purple-300 transition text-xs border border-purple-400/30 hover:border-purple-300/50 rounded px-2 py-1"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteId(c.id)}
                          className="text-red-400 hover:text-red-300 transition text-xs border border-red-400/30 hover:border-red-300/50 rounded px-2 py-1"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#131320] border border-white/10 rounded-2xl p-6 w-full max-w-lg">
            <h3 className="text-white font-semibold text-lg mb-4">{editing ? "Edit Coupon" : "New Coupon"}</h3>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-xs text-white/50 mb-1">Coupon Code</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className={inputClass + " uppercase"}
                  placeholder="SAVE20"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/50 mb-1">Discount Type</label>
                  <select
                    value={form.discountType}
                    onChange={(e) => setForm({ ...form, discountType: e.target.value as "percent" | "fixed" })}
                    className={inputClass}
                  >
                    <option value="percent">Percentage (%)</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">
                    {form.discountType === "percent" ? "Percentage" : "Amount ($)"}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={form.discountType === "percent" ? 100 : undefined}
                    value={form.discountValue}
                    onChange={(e) => setForm({ ...form, discountValue: Number(e.target.value) })}
                    className={inputClass}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/50 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">Expiration Date <span className="text-white/30">(optional)</span></label>
                  <input
                    type="date"
                    value={form.expirationDate}
                    onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Usage Limit <span className="text-white/30">(optional — leave blank for unlimited)</span></label>
                <input
                  type="number"
                  min={1}
                  value={form.usageLimit}
                  onChange={(e) => setForm({ ...form, usageLimit: e.target.value })}
                  placeholder="Unlimited"
                  className={inputClass}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} className="flex-1 border border-white/20 text-white/70 py-2 rounded-lg hover:bg-white/5 transition text-sm">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex-1 bg-purple-600 hover:bg-purple-500 disabled:opacity-60 text-white py-2 rounded-lg transition text-sm font-medium">
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#131320] border border-white/10 rounded-2xl p-6 w-full max-w-sm text-center">
            <p className="text-white font-medium mb-2">Delete this coupon?</p>
            <p className="text-white/50 text-sm mb-6">This cannot be undone. All usage history will also be deleted.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 border border-white/20 text-white/70 py-2 rounded-lg hover:bg-white/5 transition text-sm">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 rounded-lg transition text-sm font-medium">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {usageCoupon && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#131320] border border-white/10 rounded-2xl p-6 w-full max-w-xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white font-semibold text-lg font-mono">{usageCoupon.code}</h3>
                <p className="text-white/40 text-xs mt-0.5">Usage history</p>
              </div>
              <button onClick={() => setUsageCoupon(null)} className="text-white/40 hover:text-white/80 transition text-xl leading-none">×</button>
            </div>
            {loadingUsages ? (
              <div className="text-white/40 text-sm text-center py-8">Loading…</div>
            ) : usages.length === 0 ? (
              <div className="text-white/40 text-sm text-center py-8">No uses yet.</div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-white/50 text-left">
                      <th className="px-4 py-3 font-medium">Customer</th>
                      <th className="px-4 py-3 font-medium">Company</th>
                      <th className="px-4 py-3 font-medium">Order Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usages.map((u) => (
                      <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                        <td className="px-4 py-3 text-white">{u.customerName}</td>
                        <td className="px-4 py-3 text-white/60">{u.company ?? <span className="text-white/30">—</span>}</td>
                        <td className="px-4 py-3 text-white/70 whitespace-nowrap">{formatDate(u.usedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button onClick={() => setUsageCoupon(null)} className="mt-4 w-full border border-white/20 text-white/70 py-2 rounded-lg hover:bg-white/5 transition text-sm">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
